<template>
  <div id="app">
    <!-- 一级路由出口 -->
    <router-view />

    <!-- 客服新消息提示条：点击跳转客服聊天页 -->
    <div v-if="chatNotify" class="chat-notify" @click="goChat">
      <van-icon name="service-o" />
      <span class="label">客服新消息</span>
      <span class="preview">{{ chatNotify }}</span>
      <span class="close" @click.stop="chatNotify = ''">✕</span>
    </div>
  </div>
</template>

<script>
import { getChatSessionsApi, getChatMessagesApi } from '@/api/chat'
import { getChatNotifiedMsgId, setChatNotifiedMsgId } from '@/utils/chat'
import chatSocket from '@/utils/ws'

export default {
  name: 'App',
  data () {
    return {
      chatNotify: '', // 客服新消息预览（非空时展示提示条）
      chatPollTimer: null,
      chatNotifyTimer: null,
      chatNotifierReady: false, // 首次连接只建立已读基线，避免对历史消息误提醒
      wsReady: false
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    onChatPage () {
      return this.$route.path === '/chat'
    }
  },
  watch: {
    // 登录切换时重置已读基线，避免跨账号串号
    isLogin (val) {
      if (val) {
        this.chatNotifierReady = false
        this.bindChatSocket()
        chatSocket.connect('user')
      } else {
        this.wsReady = false
        chatSocket.disconnect()
        this.unbindChatSocket()
      }
      this.startChatPolling()
    },
    // 进入聊天页时停止全局提示（聊天页自行处理），离开后恢复
    onChatPage (val) {
      if (val) {
        this.chatNotify = ''
      }
    }
  },
  mounted () {
    this.bindChatSocket()
    if (this.isLogin) {
      chatSocket.connect('user')
    }
    this.startChatPolling()
    this.buildBaseline()
  },
  beforeDestroy () {
    this.stopChatPolling()
    this.unbindChatSocket()
    if (this.chatNotifyTimer) clearTimeout(this.chatNotifyTimer)
  },
  methods: {
    // ==================== WebSocket 实时通知 ====================
    bindChatSocket () {
      this.unbindChatSocket()
      // 连接建立（含重连）：补建一次已读基线，避免把断线期间的历史客服消息当成新消息
      this._onWsOpen = () => {
        this.wsReady = true
        this.buildBaseline()
      }
      this._onWsClose = () => { this.wsReady = false }
      this._onWsError = () => { this.wsReady = false }
      this._onWsMessage = (payload) => {
        const msg = payload && payload.message
        if (!msg) return
        const msgId = Number(msg.id) || 0
        // 仅提示客服（sender_type=2）发来的消息，且不在聊天页时
        if (Number(msg.sender_type) === 2 && !this.onChatPage) {
          const preview = Number(msg.msg_type) === 2 ? '[图片]' : String(msg.content || '').slice(0, 30)
          if (preview) this.showChatNotify(preview)
        }
        if (msgId > getChatNotifiedMsgId()) setChatNotifiedMsgId(msgId)
      }
      this._onWsReady = () => { this.chatNotifierReady = true }

      chatSocket.on('open', this._onWsOpen)
      chatSocket.on('close', this._onWsClose)
      chatSocket.on('error', this._onWsError)
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('ready', this._onWsReady)
    },
    unbindChatSocket () {
      if (this._onWsOpen) chatSocket.off('open', this._onWsOpen)
      if (this._onWsClose) chatSocket.off('close', this._onWsClose)
      if (this._onWsError) chatSocket.off('error', this._onWsError)
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsReady) chatSocket.off('ready', this._onWsReady)
      this._onWsOpen = null
      this._onWsClose = null
      this._onWsError = null
      this._onWsMessage = null
      this._onWsReady = null
    },
    // 建立已读基线：把当前最新消息 id 记为已读，避免历史消息被当成新消息提示
    async buildBaseline () {
      if (!this.isLogin) return
      try {
        const { data } = await getChatSessionsApi()
        const list = (data && data.list) || []
        const session = list.find(s => Number(s.status) === 1) || list[0]
        if (!session) {
          this.chatNotifierReady = true
          return
        }
        const lastId = getChatNotifiedMsgId()
        const res = await getChatMessagesApi(session.id, lastId)
        const messages = (res.data && res.data.messages) || []
        let maxId = lastId
        for (const m of messages) {
          if (m.id > maxId) maxId = m.id
        }
        if (maxId > lastId) setChatNotifiedMsgId(maxId)
        this.chatNotifierReady = true
      } catch (e) {
        // 静默失败，避免打扰用户
      }
    },
    // 兜底轮询：仅在 WebSocket 未连接时启用（间隔 15 秒）
    startChatPolling () {
      this.stopChatPolling()
      if (!this.isLogin) return
      this.chatPollTimer = setInterval(() => {
        if (chatSocket.isOpen) return
        this.pollNewChatMessages()
      }, 15000)
    },
    stopChatPolling () {
      if (this.chatPollTimer) {
        clearInterval(this.chatPollTimer)
        this.chatPollTimer = null
      }
    },
    // 轮询是否有客服发来的新消息（WebSocket 不可用时的兜底）
    async pollNewChatMessages () {
      if (!this.isLogin || this.onChatPage) return
      try {
        const { data } = await getChatSessionsApi()
        const list = (data && data.list) || []
        const session = list.find(s => Number(s.status) === 1) || list[0]

        // 首次轮询：只建立已读基线，避免对已存在的历史消息误提醒
        if (!this.chatNotifierReady) {
          await this.buildBaseline()
          return
        }

        if (!session) return

        const lastId = getChatNotifiedMsgId()
        const res = await getChatMessagesApi(session.id, lastId)
        const messages = (res.data && res.data.messages) || []
        if (!messages.length) return

        let maxId = lastId
        let preview = ''
        for (const m of messages) {
          if (m.id > maxId) maxId = m.id
          // 仅提示客服（sender_type=2）发来的消息
          if (Number(m.sender_type) === 2) {
            preview = Number(m.msg_type) === 2 ? '[图片]' : (m.content || '').slice(0, 30)
          }
        }
        setChatNotifiedMsgId(maxId)

        // 轮询请求在途时用户可能已进入聊天页，此时无需再弹提示
        if (preview && !this.onChatPage) {
          this.showChatNotify(preview)
        }
      } catch (e) {
        // 静默失败，避免打扰用户
      }
    },
    showChatNotify (preview) {
      this.chatNotify = preview
      if (this.chatNotifyTimer) clearTimeout(this.chatNotifyTimer)
      this.chatNotifyTimer = setTimeout(() => {
        this.chatNotify = ''
      }, 8000)
    },
    // 点击提示条跳转到客服聊天页
    goChat () {
      this.chatNotify = ''
      this.$router.push('/chat')
    }
  }
}
</script>

<style lang="less">
.chat-notify {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  max-width: 92%;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  border-radius: 22px;
  padding: 10px 16px;
  font-size: 13px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  cursor: pointer;

  .van-icon {
    font-size: 18px;
    margin-right: 8px;
    color: #ffd21e;
  }

  .label {
    flex-shrink: 0;
    color: #ffd21e;
    font-weight: 600;
    margin-right: 8px;
  }

  .preview {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }

  .close {
    margin-left: 10px;
    color: #bbb;
    font-size: 14px;
    padding: 4px;
  }
}
</style>

